import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Plus, Lock, Save, Camera, Users, CheckCircle2, RotateCcw, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { compressImageForUpload, uploadImageWithRetry } from '@/lib/imageUtils';
import { extractErrorMessage } from '@/lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamRosterPage() {
  const { teamToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [teamPhoto, setTeamPhoto] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchRoster(); }, [teamToken]);

  const fetchRoster = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team-roster/${teamToken}`);
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setPlayers(d.roster.length > 0 ? d.roster : [{ name: '', jersey_number: '', headshot_path: '' }]);
        setTeamPhoto(d.team_photo || '');
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const uploadImage = async (file, type) => {
    const token = localStorage.getItem('session_token');
    const endpoint = type === 'headshot' ? '/api/upload/headshot' : '/api/upload/team-photo';
    try {
      const data = await uploadImageWithRetry(API_URL, endpoint, file, token);
      return data.storage_path;
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Upload failed'));
      return null;
    }
  };

  const handleHeadshotUpload = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileId = await uploadImage(file, 'headshot');
    if (fileId) {
      setPlayers(prev => prev.map((p, i) => i === idx ? { ...p, headshot_path: fileId } : p));
      toast.success('Headshot uploaded');
    }
  };

  const handleTeamPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileId = await uploadImage(file, 'team_photo');
    if (fileId) {
      setTeamPhoto(fileId);
      toast.success('Team photo uploaded');
    }
  };

  const saveRoster = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/team-roster/${teamToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          players: players.filter(p => p.name.trim()),
          team_photo: teamPhoto
        })
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success('Roster saved!');
        // Auto-merge nudge: backend tells us if a same-jersey rename
        // propagated to historical stats_games.
        const auto = data.auto_merge;
        if (auto && Array.isArray(auto.renames) && auto.renames.length > 0) {
          const r0 = auto.renames[0];
          const extra = auto.renames.length > 1 ? ` (+${auto.renames.length - 1} more)` : '';
          toast.success(
            `Auto-merged "${r0.old}" → "${r0.new}"${extra} across ${auto.games_updated} game${auto.games_updated === 1 ? '' : 's'}.`,
            { duration: 6000 },
          );
        }
      } else {
        // FastAPI 422 returns detail as an array — stringify safely so
        // sonner never receives an object and blows up the render tree.
        let msg = 'Failed to save';
        try {
          const err = await res.json();
          if (typeof err?.detail === 'string') msg = err.detail;
          else if (Array.isArray(err?.detail)) msg = err.detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
          else if (err?.detail) msg = JSON.stringify(err.detail);
        } catch {}
        toast.error(msg);
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const toggleSubmission = async (newState) => {
    if (newState && players.filter(p => p.name.trim()).length === 0) {
      toast.error('Add at least one player before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      // Save first to ensure latest data is stored, then submit
      if (newState) {
        await fetch(`${API_URL}/api/team-roster/${teamToken}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            players: players.filter(p => p.name.trim()),
            team_photo: teamPhoto
          })
        });
      }
      const res = await fetch(`${API_URL}/api/team-roster/${teamToken}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ submitted: newState })
      });
      if (res.ok) {
        const d = await res.json();
        setData(prev => ({ ...prev, roster_submitted: d.submitted, roster_submitted_at: d.submitted ? new Date().toISOString() : null }));
        toast.success(newState ? 'Roster submitted! The organizer has been notified.' : 'Roster reopened for editing.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update submission');
      }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  // Download the current roster as CSV or PDF. Hits the public
  // token-authorized export endpoint so team contacts (who don't
  // hold an app account) can also download.
  const exportRoster = async (fmt) => {
    try {
      const res = await fetch(`${API_URL}/api/team-roster/${teamToken}/export?format=${fmt}`);
      if (!res.ok) {
        toast.error(`Export failed (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(data?.team_name || 'team').replace(/[^A-Za-z0-9._-]+/g, '_')}-Roster.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Roster downloaded as ${fmt.toUpperCase()}`);
    } catch {
      toast.error('Export failed');
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#00247D] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Team not found or link is invalid</div>;

  const submitted = !!data.roster_submitted;
  const editable = !data.roster_locked && !submitted;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>{data.team_name} ROSTER</h1>
              <p className="text-xs text-slate-500">{data.event_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#00247D]/30 text-[#00247D]" data-testid="export-roster-trigger">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportRoster('csv')} data-testid="export-roster-csv">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV (Excel / Sheets)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportRoster('pdf')} data-testid="export-roster-pdf">
                  <FileText className="w-4 h-4 mr-2" /> PDF (Printable)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {editable && (
              <Button onClick={saveRoster} disabled={saving} variant="outline" className="border-[#00247D]/30 text-[#00247D]" data-testid="save-roster-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {!data.roster_locked && (
              submitted ? (
                <Button onClick={() => toggleSubmission(false)} disabled={submitting} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="unsubmit-roster-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> {submitting ? 'Reopening...' : 'Reopen for Edits'}
                </Button>
              ) : (
                <Button onClick={() => toggleSubmission(true)} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-roster-btn">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> {submitting ? 'Submitting...' : 'Submit Roster'}
                </Button>
              )
            )}
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {submitted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 flex items-center gap-2 text-sm text-emerald-700" data-testid="roster-submitted-banner">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Roster submitted{data.roster_submitted_at ? ` on ${new Date(data.roster_submitted_at).toLocaleDateString()}` : ''}. The organizer has been notified. Click "Reopen for Edits" to make changes.</span>
          </div>
        )}
        {data.roster_locked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-center gap-2 text-sm text-red-700" data-testid="roster-locked-banner">
            <Lock className="w-4 h-4 shrink-0" />
            Roster is locked after {data.lock_after_games} game(s). Contact the tournament organizer for changes.
          </div>
        )}

        {/* Team Photo */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-[#00247D] uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
              <Camera className="w-4 h-4 inline mr-1" /> Team Photo
            </h3>
            <div className="flex items-center gap-4">
              {teamPhoto ? (
                <img src={`${API_URL}/api/files/${teamPhoto}`} alt="Team" className="w-32 h-24 object-cover rounded-lg border border-slate-200" />
              ) : (
                <div className="w-32 h-24 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                  <Users className="w-8 h-8" />
                </div>
              )}
              {editable && (
                <div>
                  <input type="file" accept="image/*" className="hidden" id="team-photo-upload" onChange={handleTeamPhotoUpload} />
                  <label htmlFor="team-photo-upload">
                    <Button variant="outline" size="sm" asChild><span><Upload className="w-3 h-3 mr-1" /> Upload Photo</span></Button>
                  </label>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Player Roster */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-[#00247D] uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              <Users className="w-4 h-4 inline mr-1" /> Players ({players.filter(p => p.name.trim()).length})
            </h3>
            <div className="space-y-3">
              {players.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`player-row-${i}`}>
                  {/* Headshot */}
                  <div className="shrink-0">
                    {p.headshot_path ? (
                      <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-slate-200" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-400">
                        <Camera className="w-5 h-5" />
                      </div>
                    )}
                    {editable && (
                      <>
                        <input type="file" accept="image/*" className="hidden" id={`headshot-${i}`} onChange={(e) => handleHeadshotUpload(i, e)} />
                        <label htmlFor={`headshot-${i}`} className="block text-center mt-1">
                          <span className="text-[10px] text-[#00247D] cursor-pointer hover:underline">Upload</span>
                        </label>
                      </>
                    )}
                  </div>

                  {/* Jersey */}
                  <Input
                    placeholder="#"
                    value={p.jersey_number}
                    onChange={e => setPlayers(prev => prev.map((pl, idx) => idx === i ? { ...pl, jersey_number: e.target.value } : pl))}
                    className="w-16 text-center font-mono"
                    disabled={!editable}
                    data-testid={`jersey-${i}`}
                  />

                  {/* Name */}
                  <Input
                    placeholder="Player name"
                    value={p.name}
                    onChange={e => setPlayers(prev => prev.map((pl, idx) => idx === i ? { ...pl, name: e.target.value } : pl))}
                    className="flex-1"
                    disabled={!editable}
                    data-testid={`player-name-${i}`}
                  />

                  {editable && players.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => setPlayers(prev => prev.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {editable && (
              <Button variant="outline" className="mt-3 w-full" onClick={() => setPlayers(prev => [...prev, { name: '', jersey_number: '', headshot_path: '' }])} data-testid="add-player-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Player
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
